import React, { type ComponentType, useCallback, useEffect, useState } from 'react';

import ToastContainer, { type ToastContainerProps } from './toast-container';
import type { BaseToastDefinition, ToastStore } from './toast-store';

export interface ToastManagerToastDefinition extends BaseToastDefinition {
  autoDismissDelay?: ToastContainerProps['autoDismissDelay'];
  disableOutsideClickDismiss?: ToastContainerProps['disableOutsideClickDismiss'];
  onDismiss?: () => void;
}

// note: toast components rendered via `ToastManager` will receive the full
// `ToastDefinition` object, but there are a few props that we don't actually
// handle in the renderer. So we omit them from the props type to make
// it more straightforward to test/storybook this component.
export type BaseToastRendererProps<TToastDefinition extends ToastManagerToastDefinition> = Omit<
  TToastDefinition,
  'animation' | 'autoDismissDelay' | 'classNames' | 'onDismiss' | 'disableOutsideClickDismiss'
> & {
  // We "override" the `onDismiss` prop because ToastManager always provides
  // a handler for this prop, but it is not required at the call site,
  // where these props are used.
  onDismiss: () => void;
};

export interface ToastManagerProps<TToastDefinition extends ToastManagerToastDefinition> {
  classNames?: {
    container?: string;
    animateIn?: string;
    animateOut?: string;
  };
  ToastRenderer: ComponentType<BaseToastRendererProps<TToastDefinition>>;
  toastStore: ToastStore<TToastDefinition>;
}

/**
 * The `ToastManager` component is responsible for rendering toasts and managing
 * their lifecycle. It is intended to be used in conjunction with a `ToastStore`
 * and a `ToastRenderer` component.
 * CAUTION: This component is not intended to be used in a render path that
 * regularly re-renders, you should strive to only render it once in your app.
 * i.e. somewhere high up in your component tree.
 */
const ToastManager = <TToastDefinition extends ToastManagerToastDefinition>({
  classNames = {},
  ToastRenderer,
  toastStore,
}: ToastManagerProps<TToastDefinition>) => {
  const [toastState, setToastState] = useState<{
    activeToastDefinition: TToastDefinition | undefined;
    isDismissing: boolean;
  }>({
    // reminder: `getToasts` can return an empty array
    activeToastDefinition: toastStore.getToasts()[0],
    isDismissing: false,
  });

  const activeToastDefinitionId = toastState.activeToastDefinition?.id;

  const {
    container: containerCss = '',
    animateIn: animateInCss = '',
    animateOut: animateOutCss = '',
  } = classNames;

  const hasAnimation = Boolean(animateInCss || animateOutCss);

  // Setup toast store subscriptions
  useEffect(() => {
    // The `toastStore.onChange` method returns a function that can be called to unsubscribe
    // the provided listener. We track these cleanup functions and invoke them in
    // the effect cleanup function.
    const cleanupFns: Array<() => void> = [];

    cleanupFns.push(
      toastStore.onChange('add', () => {
        // On toast add, if there is no active toast, get the first toast from the store and
        // set it as the active toast.
        // Otherwise, don't update state because we don't want to interrupt
        // the current toast that is being displayed.
        setToastState((prevState) => {
          if (!prevState.activeToastDefinition) {
            const toasts = toastStore.getToasts();
            return {
              activeToastDefinition: toasts[0],
              isDismissing: false,
            };
          }
          return prevState;
        });
      })
    );

    cleanupFns.push(
      toastStore.onChange('remove', ({ toastDefinition }) => {
        // On removal, if the toast that was removed from the store is the active toast, we need to
        // update the active toast to the next (first) toast in the store, if one exists.
        // We also reset the `isDismissing` flag to `false` because the toast is no longer
        // being dismissed.
        setToastState((prevState) => {
          if (prevState.activeToastDefinition?.id === toastDefinition.id) {
            const toasts = toastStore.getToasts();
            return {
              activeToastDefinition: toasts[0],
              isDismissing: false,
            };
          }
          return prevState;
        });

        // Call the `onDismiss` callback declared on the toast definition, if it exists.
        // We do this here instead of in the `handleDismiss` function so that the callback
        // is called when the toast is removed from the store, not just when the dismiss
        // handler is invoked. It's possible (though unlikely) that the toast could be
        // removed from the store by something outside of the `ToastManager`.
        toastDefinition.onDismiss?.();
      })
    );

    return () => {
      cleanupFns.forEach((fn) => fn());
    };
  }, [toastStore]);

  const handleDismiss = useCallback(() => {
    if (!activeToastDefinitionId) {
      return;
    }
    // If the dismiss handler is called and animations are provided,
    // we don't immediately remove the toast from the store because
    // that would prevent the "out" animation from playing because
    // we conditionally render the toast element based on `activeToastDefinition`.
    // Instead, we set a flag to indicate that the toast is being dismissed,
    // which will cause a re-render in which we can set the "out" animation css.
    // When that animation is complete, we handle the animationEnd event
    // and _then_ remove the toast from the store.
    if (hasAnimation) {
      setToastState((prevState) => ({
        activeToastDefinition: prevState.activeToastDefinition,
        isDismissing: true,
      }));
    } else {
      toastStore.removeToast(activeToastDefinitionId);
    }
  }, [activeToastDefinitionId, hasAnimation, toastStore]);

  const handleAnimationOutEnd = useCallback(() => {
    if (activeToastDefinitionId) {
      toastStore.removeToast(activeToastDefinitionId);
    }
  }, [activeToastDefinitionId, toastStore]);

  // If `isDismissing` is true and `animateOutCss` is defined, we need a `onAnimationEnd`
  // callback so that the animation is allowed to complete before we perform any dismiss logic.
  // Otherwise, no callback is needed, and the `animateInCss`  value will be used
  // if it is defined.
  let animationOptions: {
    className: string;
    onAnimationEnd?: () => void;
  } = {
    className: '',
    onAnimationEnd: undefined,
  };
  if (toastState.activeToastDefinition) {
    animationOptions =
      toastState.isDismissing && animateOutCss
        ? {
            className: animateOutCss,
            onAnimationEnd: () => handleAnimationOutEnd(),
          }
        : {
            className: animateInCss,
            onAnimationEnd: undefined,
          };
  }

  return toastState.activeToastDefinition ? (
    <ToastContainer
      // `key` is important here to ensure that the toast container is un-mounted/re-mounted
      // when the active toast definition changes. This is necessary to ensure that
      // the container cleans up any timers or other side-effects from the previous toast.
      key={toastState.activeToastDefinition.id}
      autoDismissDelay={toastState.activeToastDefinition.autoDismissDelay}
      className={`${containerCss} ${animationOptions.className || ''}`}
      disableOutsideClickDismiss={toastState.activeToastDefinition.disableOutsideClickDismiss}
      onAnimationEnd={animationOptions.onAnimationEnd}
      onIndirectDismiss={handleDismiss}
    >
      <ToastRenderer
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...toastState.activeToastDefinition}
        onDismiss={handleDismiss}
      />
    </ToastContainer>
  ) : null;
};

export default ToastManager;
